import { Button, Form, Input, InputNumber, Select } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/components'
import { Trans } from '@/components'
import { Metric } from '@/models/metric.ts'
interface AddMetricModalProps {
  onOk: (metric: Metric) => void
  onClose: () => void
  editingMetric?: Metric
}

type FormValuesType = {
  metricName: string
  description: string
  baselineType: number
  baselineValue: number
  retrieveAddress: string
}

export function AddMetricModal(props: AddMetricModalProps) {
  const [form] = Form.useForm<FormValuesType>()
  const { onOk, onClose, editingMetric } = props
  const { t } = useTranslation()
  useEffect(() => {
    if (editingMetric) {
      form.setFieldsValue({
        metricName: editingMetric.name,
        description: editingMetric.desc,
        baselineType: editingMetric.target.type,
        baselineValue: editingMetric.target.value,
        retrieveAddress: editingMetric.url,
      })
    } else {
      form.resetFields()
    }
  }, [editingMetric])

  const handleSubmit = (values: FormValuesType) => {
    onOk({
      type: 1,
      name: values.metricName,
      desc: values.description,
      target: {
        type: values.baselineType,
        value: values.baselineValue,
      },
      url: values.retrieveAddress,
    })
  }

  return (
    <Modal className="w-[640px]" open={true} onClose={onClose}>
      <Modal.Header>
        {editingMetric ? (
          <Trans i18nKey="scaling.createSubPage.addMetricModal.editTitle" />
        ) : (
          <Trans i18nKey="scaling.createSubPage.addMetricModal.title" />
        )}
      </Modal.Header>
      <Modal.Body>
        <Form layout="vertical" form={form} initialValues={{ baselineType: 3 }}>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="scaling.createSubPage.addMetricModal.form.metricName" />
              </div>
            }
            name="metricName"
            rules={[
              {
                required: true,
                message: t('scaling.createSubPage.addMetricModal.form.metricNamePlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t('scaling.createSubPage.addMetricModal.form.metricNamePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="scaling.createSubPage.addMetricModal.form.description" />
              </div>
            }
            name="description"
            rules={[
              {
                required: true,
                message: t('scaling.createSubPage.addMetricModal.form.descriptionPlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t('scaling.createSubPage.addMetricModal.form.descriptionPlaceholder')}
            />
          </Form.Item>
          <div className="w-full flex gap-x-4">
            <Form.Item
              label={
                <div className="t-sb text-with-star">
                  <Trans i18nKey="scaling.createSubPage.addMetricModal.form.baselineType" />
                </div>
              }
              name="baselineType"
              className="w-1/2"
              rules={[
                {
                  required: true,
                  message: t('scaling.createSubPage.addMetricModal.form.baselineTypePlaceholder'),
                },
              ]}
            >
              <Select className="h-11">
                <Select.Option value={3}>
                  <Trans i18nKey="scaling.createSubPage.addMetricModal.form.baselineTypeNumeric" />
                </Select.Option>
                <Select.Option value={1}>
                  <Trans i18nKey="scaling.createSubPage.addMetricModal.form.baselineTypeRatio" />
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={
                <div className="t-sb text-with-star">
                  <Trans i18nKey="scaling.createSubPage.addMetricModal.form.baselineValue" />
                </div>
              }
              name="baselineValue"
              className="w-1/2"
              rules={[
                {
                  required: true,
                  message: t('scaling.createSubPage.addMetricModal.form.baselineValuePlaceholder'),
                },
              ]}
            >
              <InputNumber className="w-full h-11 flex items-center" min={0} />
            </Form.Item>
          </div>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="scaling.createSubPage.addMetricModal.form.retrieveAddress" />
              </div>
            }
            name="retrieveAddress"
            rules={[
              {
                required: true,
                type: 'url',
                message: t('scaling.createSubPage.addMetricModal.form.retrieveAddressPlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t(
                'scaling.createSubPage.addMetricModal.form.retrieveAddressPlaceholder',
              )}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size="large" className="h-11 px-4 py-3 t-sb" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel" />
        </Button>
        <Button
          type="primary"
          size="large"
          className="h-11 px-4 py-3 t-sb"
          onClick={() => {
            form.validateFields().then((values) => {
              handleSubmit(values)
            })
          }}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
